import {
	KeywordAssignment,
	Maybe,
	ParagraphElementType,
	RegulatoryDocumentParagraph,
	RegulatoryDocumentParagraphElement,
	Requirement,
} from 'types';
import {TFunction} from 'react-i18next';
import Excel, {RichText} from 'exceljs';
import {
	addXLTable,
	decodeHtmlEntitiesXL,
	IArrayRoles,
	porscheFontXL,
	renderArrayXL,
	renderDateXL,
	renderHTMLStringXL,
	renderPhaseDateXL,
	renderRowItemAsLinkXL,
} from 'components/InfinityList/Excel/ExcelExportCommon';

export const renderParagraphListXL = (
	intRowCount: number,
	isRegDocDetailsPage: boolean,
	arrTables: string[],
	arrImagesName: string[],
	arrImagesUri: string[],
	arrParaFilt: RegulatoryDocumentParagraph[],
	arrRoles: IArrayRoles,
	sheet: Excel.Worksheet,
	t: TFunction,
) => {
	const tSegmentOptions = {
		ns: 'features/regulatorydocuments',
		keyPrefix: 'ParagraphsList',
	};

	const getHeadingSegment = (rawHeading: string) => {
		return t(rawHeading, tSegmentOptions);
	};

	const columnNames = [
		...(isRegDocDetailsPage
			? [getHeadingSegment('KeywordAssignments')]
			: [getHeadingSegment('Regulation'), getHeadingSegment('Version')]),
		getHeadingSegment('Paragraph'),
		getHeadingSegment('Categories'),
		getHeadingSegment('Keywords'),
		getHeadingSegment('Requirements'),
		getHeadingSegment('Tags'),
		getHeadingSegment('VehicleCategory'),
		getHeadingSegment('DriveVariants'),
		getHeadingSegment('DateNewTypes'),
		getHeadingSegment('DateNewRegistration'),
		getHeadingSegment('Comprehensive'),
		getHeadingSegment('ModelYear'),
		getHeadingSegment('PhaseIn'),
		getHeadingSegment('PhaseOut'),
		...(isRegDocDetailsPage ? [getHeadingSegment('ChangeInfoType')] : []),
		getHeadingSegment('Attachments'),
		getHeadingSegment('Summary'),
	];

	const columns = columnNames.map(x => {
		return {name: x, filterButton: true};
	});

	let row = sheet.getRow(intRowCount);

	addXLTable('tableSegments', intRowCount, sheet, columns);

	const table = sheet.getTable('tableSegments');

	if (arrParaFilt.length === 0) {
		table.addRow(new Array(columnNames.length).fill(''));
	}

	let intTableCount = 0;

	intRowCount++;
	for (let ii = 0; ii < arrParaFilt.length; ii++) {
		const currentParagraph = arrParaFilt[ii];
		const regulationNumber =
			currentParagraph.parent?.regulation?.regulationNumber ?? '_';
		const regulationVerion = currentParagraph.parent?.name;

		const keywordStatus = renderParagraphKeywordStatusXL(
			currentParagraph.keywordAssignments,
			getHeadingSegment,
		);

		const category = renderArrayXL(currentParagraph.categories);

		const keyword = renderArrayXL(currentParagraph.keywords);

		const tag = renderArrayXL(currentParagraph.tags);

		const vehClass = renderArrayXL(currentParagraph.vehicleCategories);

		const drivetrain = renderArrayXL(currentParagraph.driveVariants);

		const dateNT = renderDateXL(currentParagraph.dateNewTypes);

		const dateNF = renderDateXL(currentParagraph.dateNewRegistration);

		const dateUB = renderDateXL(currentParagraph.comprehensive);

		const modelyear = String(currentParagraph.modelYear ?? '');

		const phaseIn = renderPhaseDateXL(currentParagraph.phaseIn);

		const phaseOut = renderPhaseDateXL(currentParagraph.phaseOut ?? []);

		const changeInfo = t(currentParagraph.changeInfoType, {
			ns: 'common/enums',
			keyPrefix: 'ChangeInfoType',
		});

		const attachs = (currentParagraph.attachments ?? [])
			.map(x => x.file?.fileName ?? '')
			.join('\n');

		const comment = renderHTMLStringXL('', currentParagraph.summary ?? '');

		const arrRichTexts = renderParagraphContentXL(
			currentParagraph.enumeration,
			currentParagraph.elements,
			t,
		);
		const arrRichTextsReq = renderParagaphRequirementsXL(
			currentParagraph.requirements,
			arrRoles,
		);

		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};
		table.addRow([
			...(isRegDocDetailsPage
				? [keywordStatus]
				: [regulationNumber, regulationVerion]),
			{richText: arrRichTexts, wrapText: true, vertical: 'top'},
			category,
			keyword,
			{richText: arrRichTextsReq, wrapText: true, vertical: 'top'},
			tag,
			vehClass,
			drivetrain,
			dateNT,
			dateNF,
			dateUB,
			modelyear,
			phaseIn,
			phaseOut,
			...(isRegDocDetailsPage ? [changeInfo] : []),
			attachs,
			{richText: comment},
		]);
		intRowCount++;

		intRowCount = renderParagraphImagesXL(
			currentParagraph.level,
			intRowCount,
			intTableCount,
			isRegDocDetailsPage,
			arrImagesUri,
			arrImagesName,
			arrTables,
			currentParagraph.elements,
			sheet,
			table,
			t,
		);
	}

	table.commit();
};

const renderParagraphKeywordStatusXL = (
	keywordAssignments: Maybe<KeywordAssignment[]> | undefined,
	getHeadingSegment: (rawHeading: string) => string,
) => {
	return (keywordAssignments ?? [])
		.map(keywordAssign =>
			keywordAssign.vexClusterAssignments
				.map(
					iVexAssign =>
						keywordAssign.keyword.name +
						'|' +
						getHeadingSegment(iVexAssign.keywordStatus) +
						'|' +
						iVexAssign.vexCluster.name,
				)
				.join(';\n'),
		)
		.join(';\n');
};

const renderParagraphContentXL = (
	enumeration: string,
	elements: RegulatoryDocumentParagraphElement[],
	t: TFunction,
) => {
	const arrRichTexts: RichText[] = [
		{
			font: {...porscheFontXL()},
			text: enumeration + '\n',
		},
	];

	for (let jj = 0; jj < elements.length; jj++) {
		if (
			elements[jj].type === ParagraphElementType.Text &&
			elements[jj].isHeader == false
		) {
			let text = decodeHtmlEntitiesXL(elements[jj].text ?? '');

			if (text.startsWith('VKO_PDF_DISMANTLED')) {
				text = t(
					text === 'VKO_PDF_DISMANTLED_FALSE'
						? 'DismantledFalseXL'
						: 'DismantledTrue',
					{
						ns: 'features/regulatorydocuments',
						keyPrefix: 'RegulatoryDocumentDetailsPage',
					},
				);
			}

			arrRichTexts.push({
				font: {...porscheFontXL(), bold: false},
				text: text,
			});
		}
		if (elements[jj].type === ParagraphElementType.Html) {
			const renderedHTML = renderHTMLStringXL(
				'',
				elements[jj].text ?? '',
			).richText;
			(renderedHTML ?? []).forEach(x => arrRichTexts.push(x));
		}
		if (elements[jj].type === ParagraphElementType.NewLine) {
			arrRichTexts.push({font: {...porscheFontXL(), bold: false}, text: '\n'});
		}
	}
	return arrRichTexts;
};

const renderParagaphRequirementsXL = (
	requirements: Requirement[],
	arrRoles: IArrayRoles,
) => {
	const arrRichTextsReq = [];
	let arrRequirementsSorted = [] as Requirement[];
	if (!arrRoles.isReader) {
		arrRequirementsSorted = requirements.slice();
		arrRequirementsSorted.sort(function (a, b) {
			return a.requirementId! < b.requirementId! ? -1 : 1;
		});
	}
	for (let jj = 0; jj < arrRequirementsSorted.length; jj++) {
		arrRichTextsReq.push({
			font: porscheFontXL(),
			text:
				arrRequirementsSorted[jj].name +
				' (' +
				arrRequirementsSorted[jj].requirementId +
				')\n\n',
		});
	}
	return arrRichTextsReq;
};

const renderParagraphImagesXL = (
	level: number,
	intRowCount: number,
	intTableCount: number,
	isRegDocDetailsPage: boolean,
	arrImagesUri: string[],
	arrImagesName: string[],
	arrTables: string[],
	elements: RegulatoryDocumentParagraphElement[],
	sheet: Excel.Worksheet,
	table: Excel.Table,
	t: TFunction,
) => {
	const arrLinks: string[] = [];
	const arrLinkText: string[] = [];

	const ns = 'components/EntityList/ExcelExport';
	for (let jj = 0; jj < elements.length; jj++) {
		if (
			String(elements[jj].type) === 'IMAGE' ||
			String(elements[jj].type) === 'TABLE'
		) {
			arrImagesUri.push(String(elements[jj].asset?.uri));
			arrImagesName.push(
				String(
					(elements[jj].asset?.fileName.split('.')[0] as string) + '.html',
				),
			);
			arrLinks.push('assets/' + arrImagesName[arrImagesName.length - 1]);
			arrLinkText.push(t('LinkImage', {ns}));
		}

		if (String(elements[jj].type) === 'HTML_TABLE') {
			arrTables.push(String(elements[jj].text));
			arrLinks.push('assets/html_table_' + intTableCount + '.html');
			arrLinkText.push(t('LinkTable', {ns}));
			intTableCount++;
		}
	}

	for (let jj = 0; jj < arrLinks.length; jj++) {
		intRowCount = renderRowItemAsLinkXL(
			intRowCount,
			arrLinkText[jj],
			arrLinks[jj],
			isRegDocDetailsPage ? 'C' : 'D',
			isRegDocDetailsPage ? 1 : 2,
			sheet,
			table,
			t,
		);
	}
	return intRowCount;
};
